import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { Field } from "@/components/common";
import { HelpTip } from "@/components/HelpTip";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NumInput } from "@/components/NumInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, brl, errMsg, itemCost, pct, channelPct, UNITS } from "@/lib/api";
import { useApi } from "@/hooks/useApi";
import { IngredientCombobox } from "@/components/IngredientCombobox";
import { ImageUpload, shrinkImage } from "@/components/ImageUpload";

const EMPTY = { name: "", category: "", portions: 1, notes: "", items: [] };
const NO_IMG = { file: null, removed: false };

export const RecipeDialog = ({ open, onOpenChange, recipe, ingredients, onSaved }) => {
  const { user } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [img, setImg] = useState(NO_IMG);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setF(recipe ? { ...recipe, items: recipe.items.map((i) => ({ ingredient_id: i.ingredient_id, quantity: i.quantity, unit: i.unit })) } : EMPTY);
    setImg(NO_IMG);
  }, [recipe, open]);

  const ingMap = useMemo(() => Object.fromEntries((ingredients || []).map((i) => [i.id, i])), [ingredients]);
  const total = f.items.reduce((a, it) => a + itemCost(ingMap[it.ingredient_id], Number(it.quantity) || 0, it.unit), 0);
  const cpp = total / Math.max(Number(f.portions) || 1, 1);
  const { data: channels } = useApi(open ? "/channels" : null);
  const defCh = (channels || []).find((c) => c.is_default) || channels?.[0];
  // Preço padrão é definido só em Precificação; a ficha apenas espelha o valor salvo
  const priceNum = Number(recipe?.sale_price) || 0;
  const liveMargin = priceNum > 0 ? ((priceNum - cpp - (priceNum * channelPct(defCh)) / 100) / priceNum) * 100 : null;

  const setItem = (idx, patch) => setF({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) });
  const pickIngredient = (idx, id) => setItem(idx, { ingredient_id: id, unit: ingMap[id]?.unit || "un" });
  const addItem = () => {
    const first = ingredients?.[0];
    setF({ ...f, items: [...f.items, { ingredient_id: first?.id || "", quantity: "", unit: first?.unit || "un" }] });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.items.length) return toast.error("Adicione ao menos um ingrediente");
    setSaving(true);
    try {
      const body = { name: f.name, category: f.category || "", portions: Number(f.portions) || 1, notes: f.notes || "",
        items: f.items.filter((i) => i.ingredient_id).map((i) => ({ ingredient_id: i.ingredient_id, quantity: Number(i.quantity) || 0, unit: i.unit })) };
      let r = recipe?.id ? await api.put(`/recipes/${recipe.id}`, body) : await api.post("/recipes", body);
      toast.success(recipe?.id ? "Ficha atualizada" : "Ficha técnica criada");
      try {
        if (img.file) {
          const fd = new FormData();
          fd.append("file", await shrinkImage(img.file));
          r = await api.post(`/recipes/${r.data.id}/image`, fd);
        } else if (img.removed && recipe?.image) {
          r = await api.delete(`/recipes/${r.data.id}/image`);
        }
      } catch (err) { toast.error(`Ficha salva, mas a imagem falhou: ${errMsg(err)}`); }
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="recipe-dialog">
        <DialogHeader><DialogTitle className="font-display">{recipe?.id ? "Editar ficha técnica" : "Nova ficha técnica"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 space-y-4">
            <ImageUpload recipe={recipe} file={img.file} removed={img.removed} onFile={(file) => setImg({ file, removed: false })} onRemove={() => setImg({ file: null, removed: true })} />
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nome do prato"><Input data-testid="recipe-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ex.: X-Burger" /></Field>
              <Field label="Categoria"><Input data-testid="recipe-category" value={f.category || ""} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="Hambúrguer, Pizza..." /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Rende (porções)" help={<HelpTip title="Rendimento" testId="help-portions">Quantas porções essa receita produz. O custo por porção = custo total ÷ rendimento.</HelpTip>}>
                <NumInput data-testid="recipe-portions" min="1" step="1" value={f.portions} onChange={(e) => setF({ ...f, portions: e.target.value })} />
              </Field>
              <Field label="Preço de venda padrão (R$)" help={<HelpTip title="Preço padrão" testId="help-recipe-price">Definido em Precificação → Por produto (canal padrão{defCh ? ` · ${defCh.name}` : ""}). A ficha técnica só espelha o valor salvo lá; para alterar, use a aba Precificação.</HelpTip>}>
                <div data-testid="recipe-price-readonly" aria-readonly="true" className={`h-9 rounded-md border border-dashed px-3 flex items-center justify-between gap-2 text-sm ${priceNum > 0 ? "border-border bg-secondary/40" : "border-border/70 bg-secondary/20"}`}>
                  {priceNum > 0
                    ? <><span className="num font-bold" data-testid="recipe-price-value">{brl(priceNum)}</span><span className="text-[11px] text-muted-foreground truncate">via Precificação</span></>
                    : <span className="text-muted-foreground truncate" data-testid="recipe-price-empty">— Defina o preço na aba Precificação</span>}
                </div>
              </Field>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Ingredientes</p>
                <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="recipe-add-item" disabled={!ingredients?.length}><Plus className="w-4 h-4 mr-1" />Adicionar</Button>
              </div>
              {!ingredients?.length && <p className="text-xs text-amber-500">Cadastre insumos primeiro para montar a ficha.</p>}
              {f.items.map((it, idx) => (
                <div key={idx} className="flex flex-wrap sm:flex-nowrap gap-2 items-center rounded-lg border border-border/60 p-2" data-testid={`recipe-item-${idx}`}>
                  <div className="w-full sm:flex-1 min-w-0">
                    <IngredientCombobox ingredients={ingredients} value={it.ingredient_id} onChange={(v) => pickIngredient(idx, v)} testId={`recipe-item-ingredient-${idx}`} />
                  </div>
                  <div className="w-24 shrink-0"><NumInput data-testid={`recipe-item-qty-${idx}`} min="0" step="any" value={it.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })} placeholder="Qtd" className="num" /></div>
                  <div className="w-20 shrink-0">
                    <Select value={it.unit} onValueChange={(v) => setItem(idx, { unit: v })}>
                      <SelectTrigger data-testid={`recipe-item-unit-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 sm:flex-none sm:w-24 num text-xs text-right text-muted-foreground whitespace-nowrap" data-testid={`recipe-item-cost-${idx}`}>{brl(itemCost(ingMap[it.ingredient_id], Number(it.quantity) || 0, it.unit))}</div>
                  <button type="button" className="ml-3 w-9 h-9 shrink-0 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-rose-500 hover:border-rose-500/50 hover:bg-rose-500/10 transition-colors" onClick={() => setF({ ...f, items: f.items.filter((_, i) => i !== idx) })} data-testid={`recipe-item-remove-${idx}`} aria-label="Remover ingrediente"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-2xl border border-border bg-secondary/40 p-5 space-y-4 sticky top-0">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Custo em tempo real</p>
              <div><p className="text-sm text-muted-foreground">Custo total da receita</p><p className="num text-2xl font-bold" data-testid="recipe-live-total">{brl(total)}</p></div>
              <div><p className="text-sm text-muted-foreground">Custo por porção</p><p className="num text-3xl font-extrabold text-primary" data-testid="recipe-live-cpp">{brl(cpp)}</p></div>
              <div className="border-t border-border pt-3 space-y-1">
                <p className="text-sm text-muted-foreground flex items-center gap-2">Margem real com o preço padrão <HelpTip title="Margem real" testId="help-suggested">Calculada com o preço padrão salvo em Precificação e as taxas do canal padrão{defCh ? ` (${defCh.name})` : ""}: (preço − custo − taxas) ÷ preço. A margem desejada é decisão sua.</HelpTip></p>
                <p className={`num text-xl font-bold ${liveMargin == null ? "text-muted-foreground" : liveMargin < 0 ? "text-rose-500" : "text-emerald-500"}`} data-testid="recipe-live-margin">{liveMargin == null ? "—" : pct(liveMargin)}</p>
                {liveMargin == null && <p className="text-xs text-muted-foreground" data-testid="recipe-live-safety">defina o preço na aba Precificação para ver a margem</p>}
                {priceNum > 0 && <p className="text-xs text-muted-foreground">CMV no preço atual: <span className="num">{((cpp / priceNum) * 100).toFixed(1)}%</span></p>}
              </div>
              <Button data-testid="recipe-save" type="submit" className="w-full" disabled={saving}>{saving ? "Salvando..." : "Salvar ficha técnica"}</Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
